import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styles from './InfoTooltip.module.css';

const TOOLTIP_WIDTH = 250;
const VIEWPORT_MARGIN = 8;

// Portal em document.body, posição em pixels: evita corte por overflow:hidden de ancestrais (ex.: Card).
const InfoTooltip = ({ text, className = '' }) => {
  const iconRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const icon = iconRef.current;
    if (!icon) return;

    const rect = icon.getBoundingClientRect();
    const left = Math.min(
      Math.max(rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2, VIEWPORT_MARGIN),
      window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN
    );
    setPosition({ top: rect.top, left });
  }, []);

  const show = () => {
    updatePosition();
    setVisible(true);
  };
  const hide = () => setVisible(false);

  useEffect(() => {
    if (!visible) return undefined;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible, updatePosition]);

  if (!text) return null;

  return (
    <span
      ref={iconRef}
      className={`${styles.tooltipContainer} ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      tabIndex={0}
    >
      <span className={styles.infoIcon} aria-label="Informação adicional">i</span>
      {createPortal(
        <span
          className={styles.tooltipText}
          style={{
            top: position.top,
            left: position.left,
            visibility: visible ? 'visible' : 'hidden',
            opacity: visible ? 1 : 0,
          }}
        >
          {text}
        </span>,
        document.body
      )}
    </span>
  );
};

InfoTooltip.propTypes = {
  text: PropTypes.string,
  className: PropTypes.string,
};

export default InfoTooltip;
